import React, { useMemo, useState } from 'react';
import moment from 'moment';
import { Stack, Typography } from '@mui/material';
import { useForm, useWatch } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { accountSelectors } from 'redux/accountReducer';
import { LENDER_ACTIVITY_DEFAULT_FILTERS, LENDER_APPS_DEFAULT_SORTING } from '../../../constants';
import { mapFiltersToReportInput } from '../../../lib';
import { LenderActivityFilterFormValues, LenderAppsSorting } from '../../../types';
import { useLenderActivityDashboard } from '../../../hooks/useLenderActivityDashboard';
import { LenderActivityFilters } from './LenderActivityFilters';
import { SummaryCards } from './SummaryCards';
import { LenderApps } from './LenderApps';
import { CreditApps } from './CreditApps';
import { Declines } from './Declines';

export const LenderActivityDashboard = () => {
  const account = useSelector(accountSelectors.account);

  const { control, setValue } = useForm<LenderActivityFilterFormValues>({
    defaultValues: LENDER_ACTIVITY_DEFAULT_FILTERS,
  });

  const filters = useWatch({ control }) as LenderActivityFilterFormValues;
  const input = useMemo(() => mapFiltersToReportInput(filters), [filters]);

  const [lenderSorting, setLenderSorting] = useState<LenderAppsSorting>(LENDER_APPS_DEFAULT_SORTING);

  const {
    summary,
    byCountry,
    byLender,
    byState,
    byDealer,
    dealerTotals,
    byDeclineReason,
    isSummaryLoading,
    isCountryLoading,
    isLenderLoading,
    isStateLoading,
    isDealerLoading,
    isDeclineReasonLoading,
  } = useLenderActivityDashboard(input, lenderSorting);

  const handleRangeChange = async (range: { endDate: moment.Moment; startDate: moment.Moment }) => {
    setValue('from', range.startDate);
    setValue('to', range.endDate);
  };

  return (
    <Stack gap="1rem">
      <LenderActivityFilters control={control} onRangeChange={handleRangeChange} />

      <SummaryCards summary={summary} loading={isSummaryLoading} />

      <LenderApps
        rows={byLender}
        loading={isLenderLoading}
        sorting={lenderSorting}
        onSortingChange={setLenderSorting}
      />

      <CreditApps
        stateRows={byState}
        dealerRows={byDealer}
        dealerTotalCreditApps={dealerTotals.totalCreditApps}
        dealerTotalUnits={dealerTotals.totalUnits}
        isStateLoading={isStateLoading}
        isDealerLoading={isDealerLoading}
      />

      <Declines
        byCountry={byCountry}
        declineReasonRows={byDeclineReason}
        loading={isCountryLoading}
        isDeclineReasonLoading={isDeclineReasonLoading}
      />
    </Stack>
  );
};
